import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { apiClient } from "@/App";
import { format } from "date-fns";
import { gigLocalDate } from "@/utils/dateUtils";

// Embeddable public-gigs widget. Rendered inside an <iframe> on a musician's
// own website (see Settings → Website widget for the snippet), so it must be
// fully self-contained: inline styles only — no dependency on the app's Tailwind
// classes or theme — and no auth. Data comes from the public /musician/:username
// endpoint (upcoming public gigs, opt-in via show_public_gigs).

const FRETLESS_ORANGE = "#F5891F";

// Two small palettes; the host picks via ?theme=light|dark. Background defaults
// to transparent so the widget inherits the host page's surface unless the host
// sets a solid one explicitly.
const THEMES = {
  light: {
    text: "#1a1a1a",
    muted: "#6b6b6b",
    border: "rgba(0,0,0,0.10)",
    cardBg: "rgba(0,0,0,0.02)",
  },
  dark: {
    text: "#f1efe9",
    muted: "#a2a09a",
    border: "rgba(255,255,255,0.14)",
    cardBg: "rgba(255,255,255,0.04)",
  },
};

function parseGigDate(dateStr) {
  const local = gigLocalDate(dateStr);
  if (!local) return null;
  const [y, m, d] = local.split("-");
  if (!y || !m || !d) return null;
  return new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
}

const toAbsoluteUrl = (url) => {
  const u = (url || "").trim();
  if (!u) return null;
  return /^https?:\/\//i.test(u) ? u : `https://${u}`;
};

export default function EmbedGigs() {
  const { username } = useParams();
  const [params] = useSearchParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const themeName = params.get("theme") === "dark" ? "dark" : "light";
  const theme = THEMES[themeName];
  const accent = params.get("accent") ? `#${params.get("accent").replace(/^#/, "")}` : FRETLESS_ORANGE;
  const limit = Math.min(parseInt(params.get("limit"), 10) || 10, 25);
  const showHeader = params.get("header") !== "false";

  // Make the iframe document transparent so the widget blends into the host
  // page (the SPA body otherwise paints the app background). Scoped to this
  // route's document, which is the isolated iframe document.
  useEffect(() => {
    const prevHtml = document.documentElement.style.background;
    const prevBody = document.body.style.background;
    const prevMargin = document.body.style.margin;
    document.documentElement.style.background = "transparent";
    document.body.style.background = "transparent";
    document.body.style.margin = "0";
    return () => {
      document.documentElement.style.background = prevHtml;
      document.body.style.background = prevBody;
      document.body.style.margin = prevMargin;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get(`/musician/${username}`);
        if (!cancelled) setProfile(res.data);
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [username]);

  const wrap = {
    fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
    color: theme.text,
    padding: "12px",
    boxSizing: "border-box",
    width: "100%",
  };

  if (loading) {
    return (
      <div style={{ ...wrap, textAlign: "center", padding: "32px 12px", color: theme.muted, fontSize: 14 }}>
        Loading gigs…
      </div>
    );
  }

  const gigs = (profile?.upcoming_gigs || []).slice(0, limit);

  if (error || !profile) {
    return (
      <div style={{ ...wrap, textAlign: "center", padding: "32px 12px", color: theme.muted, fontSize: 14 }}>
        Gigs unavailable.
      </div>
    );
  }

  return (
    <div style={wrap} data-testid="embed-gigs">
      {showHeader && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, letterSpacing: "0.08em", textTransform: "uppercase", color: accent, fontWeight: 700 }}>
            Upcoming Shows
          </div>
          {(profile.artist_names || profile.name) && (
            <div style={{ fontSize: 18, fontWeight: 700, marginTop: 2 }}>
              {profile.artist_names || profile.name}
            </div>
          )}
        </div>
      )}

      {gigs.length === 0 ? (
        <div style={{ color: theme.muted, fontSize: 14, padding: "20px 0" }}>
          No upcoming shows right now — check back soon.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {gigs.map((gig, i) => {
            const d = parseGigDate(gig.date);
            const ticket = toAbsoluteUrl(gig.ticket_link);
            return (
              <div
                key={i}
                data-testid={`embed-gig-${i}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "10px 12px",
                  border: `1px solid ${theme.border}`,
                  borderRadius: 10,
                  background: theme.cardBg,
                }}
              >
                {/* Date block */}
                <div style={{ textAlign: "center", minWidth: 44, flexShrink: 0 }}>
                  <div style={{ fontSize: 11, textTransform: "uppercase", color: accent, fontWeight: 700, lineHeight: 1.2 }}>
                    {d ? format(d, "MMM") : "TBA"}
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 700, lineHeight: 1.1 }}>
                    {d ? format(d, "d") : "–"}
                  </div>
                </div>
                {/* Details */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {gig.venue_name || "Venue TBA"}
                  </div>
                  <div style={{ fontSize: 12, color: theme.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {[gig.city, gig.start_time].filter(Boolean).join(" · ")}
                  </div>
                </div>
                {/* Tickets */}
                {ticket && (
                  <a
                    href={ticket}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      flexShrink: 0,
                      background: accent,
                      color: "#fff",
                      textDecoration: "none",
                      fontSize: 12,
                      fontWeight: 600,
                      padding: "6px 12px",
                      borderRadius: 999,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Tickets{gig.ticket_price ? ` · ${gig.ticket_price}` : ""}
                  </a>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Attribution — also a soft signup funnel. ?ref carries the referrer. */}
      <div style={{ marginTop: 12, textAlign: "right" }}>
        <a
          href={`https://www.fretless.life/?ref=${encodeURIComponent(username)}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ fontSize: 11, color: theme.muted, textDecoration: "none" }}
        >
          Powered by <span style={{ color: accent, fontWeight: 700 }}>Fretless</span>
        </a>
      </div>
    </div>
  );
}
